import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { PaymentOption } from '../utils/types';
import { getPaymentOptions } from '../../auth/_requests';
import { useAuth } from '../../auth/Auth';

interface PaymentOptionsContextType {
  paymentOptions: PaymentOption[];
  loading: boolean;
  refreshPaymentOptions: () => Promise<void>;
}

const PaymentOptionsContext = createContext<PaymentOptionsContextType | undefined>(undefined);

export const usePaymentOptions = () => {
  const context = useContext(PaymentOptionsContext);
  if (!context) {
    throw new Error('usePaymentOptions must be used within a PaymentOptionsProvider');
  }
  return context;
};

interface PaymentOptionsProviderProps {
  children: ReactNode;
}

export const PaymentOptionsProvider: React.FC<PaymentOptionsProviderProps> = ({ children }) => {
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { auth } = useAuth();
  const fetchPaymentOptions = useCallback(async () => {
    if (!auth?.data?.token) return;
    setLoading(true);
    try {
      const options = await getPaymentOptions(auth?.data?.token);

      if (options.length === 1) {
        localStorage.setItem('selectedPaymentOption', options[0].id);
      }
      setPaymentOptions([...options]);
    } catch (error) {
      console.error('Error fetching payment options:', error);
    } finally {
      setLoading(false);
    }
  }, [auth?.data?.token]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchPaymentOptions();
    };
    fetchData();
  }, [fetchPaymentOptions]);

  return (
    <PaymentOptionsContext.Provider
      value={{
        paymentOptions,
        loading,
        refreshPaymentOptions: async () => await fetchPaymentOptions(),
      }}
    >
      {children}
    </PaymentOptionsContext.Provider>
  );
};
